import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { TierBadge } from '../components/ui/TierBadge';

export default function ReferEarn() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="eyebrow mb-3 text-violet-600">AFFILIATE PROGRAM</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
          Earn on every friend <span className="text-gradient">— and every friend's friend</span>
        </h1>
        <p className="mt-4 text-ink-700 text-lg max-w-2xl mx-auto">
          Two levels of rewards. Real USDT to your wallet on direct referrals,
          plus passive USDT on your team's referrals. Bonus AIML tokens on top — your
          stake in the project growing. Referrals are yours forever.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/auth/signup">
            <Button variant="gradient" size="lg">
              Get my referral link
            </Button>
          </Link>
          <Link to="/buy">
            <Button variant="outline" size="lg">
              Buy $AIML
            </Button>
          </Link>
        </div>
      </div>

      {/* The 3 tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {/* Bronze */}
        <Card className="!border-amber-200 !bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="BRONZE" size="lg" />
            </div>
            <div className="eyebrow text-amber-700">Starting line</div>
            <h3 className="font-display text-2xl font-bold mt-2">$100 personal buy</h3>

            <div className="mt-6 text-left bg-white/60 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Direct (Level 1)
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl">10%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl">2%</span>
              </div>

              <div className="pt-3 border-t border-amber-200" />

              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                Indirect (Level 2)
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl">2%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl">2%</span>
              </div>
            </div>

            <p className="text-xs text-ink-700 mt-4">
              Buy $100 of AIML and your referral link goes live. Start earning on direct
              and indirect referrals from day one.
            </p>
          </div>
        </Card>

        {/* Silver */}
        <Card className="!border-slate-300 !bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="SILVER" size="lg" />
            </div>
            <div className="eyebrow text-slate-700">Tier upgrade</div>
            <h3 className="font-display text-2xl font-bold mt-2">$300 personal buy</h3>

            <div className="mt-6 text-left bg-white/60 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Direct (Level 1)
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl">20%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl">4%</span>
              </div>

              <div className="pt-3 border-t border-slate-200" />

              <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                Indirect (Level 2)
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl">2%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl">2%</span>
              </div>
            </div>

            <p className="text-xs text-ink-700 mt-4">
              Top up to $300 and your direct rate jumps to 20% cash + 4% tokens
              — applies to all your future referrals.
            </p>
          </div>
        </Card>

        {/* Founders Circle */}
        <Card className="!border-violet-300 !bg-gradient-to-br from-magenta-50 via-violet-50 to-azure-50 relative overflow-hidden">
          <div className="absolute top-0 right-0 px-3 py-1 bg-gradient-to-r from-magenta-500 via-violet-500 to-azure-500 text-white text-[10px] uppercase tracking-widest rounded-bl-2xl">
            Leadership tier
          </div>
          <div className="text-center">
            <div className="mb-3">
              <TierBadge tier="FOUNDERS_CIRCLE" size="lg" />
            </div>
            <div className="eyebrow text-violet-700">Top performers</div>
            <h3 className="font-display text-xl font-bold mt-2">$3,000 team volume</h3>

            <div className="mt-6 text-left bg-white/60 rounded-xl p-4 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-violet-700">
                Direct (Level 1)
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl">22%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl">6%</span>
              </div>

              <div className="pt-3 border-t border-violet-200" />

              <div className="text-xs font-semibold uppercase tracking-wider text-violet-700">
                Indirect (Level 2) <span className="text-[10px] font-normal italic">leadership</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">USDT cash</span>
                <span className="font-display font-bold text-xl text-gradient">3%</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-ink-600">AIML bonus</span>
                <span className="font-display font-bold text-xl text-gradient">3%</span>
              </div>
            </div>

            <p className="text-xs text-ink-700 mt-4">
              Build a $3,000 team (your referrals + their referrals) and join the inner
              circle — top rates plus an upgraded 3% override on every indirect sale.
            </p>
          </div>
        </Card>
      </div>

      {/* Level 1 vs Level 2 explainer (NEW) */}
      <Card className="mb-16 !bg-gradient-to-br from-violet-50 to-magenta-50 !border-violet-200">
        <div className="text-center mb-6">
          <div className="eyebrow text-violet-600">TWO LEVELS OF EARNING</div>
          <h2 className="font-display text-3xl font-bold mt-2">Level 1 vs Level 2 — what's the difference?</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/80 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🎯</span>
              <h3 className="font-display font-bold text-lg">Level 1 — your direct referrals</h3>
            </div>
            <p className="text-sm text-ink-700 mb-3">
              A friend clicks your link, signs up, and buys $AIML.
              <strong> You earn at your full tier rate.</strong>
            </p>
            <p className="text-xs text-ink-600 italic">
              Active income. You did the recruiting, you get the bigger cut.
            </p>
            <div className="mt-4 p-3 bg-violet-50 rounded-lg text-xs text-ink-700">
              Example: at <strong>Silver</strong>, on a friend's $1,000 buy you earn
              <strong className="text-violet-700"> $200 USDT + 4% AIML bonus.</strong>
            </div>
          </div>

          <div className="bg-white/80 rounded-2xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🌱</span>
              <h3 className="font-display font-bold text-lg">Level 2 — your team's referrals</h3>
            </div>
            <p className="text-sm text-ink-700 mb-3">
              Your friend brings their own friend, who then buys $AIML.
              <strong> You still earn — passively.</strong>
            </p>
            <p className="text-xs text-ink-600 italic">
              Passive income. You didn't recruit them, but they're in your team's chain,
              so you keep earning as your team grows their own teams.
            </p>
            <div className="mt-4 p-3 bg-magenta-50 rounded-lg text-xs text-ink-700">
              Example: at <strong>Founders Circle</strong>, on your team's $1,000 indirect
              buy you earn <strong className="text-violet-700"> $30 USDT + 3% AIML bonus.</strong>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-white/80 rounded-xl text-center text-sm text-ink-700">
          <strong>This is what makes the program scale.</strong> Once you've recruited a few people,
          coach them to recruit too — every sale in your team's downline pays you forever.
        </div>
      </Card>

      {/* How it works */}
      <div className="mb-16">
        <div className="text-center mb-8">
          <div className="eyebrow text-violet-600">HOW IT WORKS</div>
          <h2 className="font-display text-3xl font-bold mt-2">Four steps. That's it.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              n: '1',
              title: 'Buy $100 in AIML',
              body: "Your one-time stake to become Bronze. You own those tokens — they're yours regardless of referrals.",
            },
            {
              n: '2',
              title: 'Get your referral link',
              body: 'Automatically generated when you hit Bronze. Share via WhatsApp, social media, anywhere.',
            },
            {
              n: '3',
              title: 'Earn on two levels',
              body: 'Every direct buy pays you. Every indirect buy (your team\'s referrals) pays you too — passive USDT + AIML.',
            },
            {
              n: '4',
              title: 'Climb the tiers',
              body: 'Top up to Silver. Coach your team to $3k volume for Founders Circle. Your rate goes up — forever.',
            },
          ].map((step) => (
            <Card key={step.n} className="text-center">
              <div className="text-3xl font-display font-bold text-gradient mb-3">{step.n}</div>
              <h3 className="font-display font-bold text-lg mb-2">{step.title}</h3>
              <p className="text-sm text-ink-700">{step.body}</p>
            </Card>
          ))}
        </div>
      </div>

      {/* What you actually earn — split into L1 and L2 tables */}
      <Card className="mb-16 !bg-gradient-to-br from-violet-50 via-white to-magenta-50">
        <div className="text-center">
          <div className="eyebrow text-violet-600">REAL NUMBERS</div>
          <h2 className="font-display text-3xl font-bold mt-2 mb-6">
            What you earn at each tier
          </h2>
        </div>

        {/* Level 1 table */}
        <div className="mb-8">
          <h3 className="font-display font-semibold text-lg mb-3">
            🎯 Direct referrals (Level 1) — friend buys through your link
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-paper-300">
                  <th className="px-3 py-3 text-left eyebrow">Friend buys</th>
                  <th className="px-3 py-3 text-right eyebrow">Bronze earns</th>
                  <th className="px-3 py-3 text-right eyebrow">Silver earns</th>
                  <th className="px-3 py-3 text-right eyebrow">Founders Circle earns</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { amt: 100,  b: 10,   s: 20,   fc: 22   },
                  { amt: 500,  b: 50,   s: 100,  fc: 110  },
                  { amt: 1000, b: 100,  s: 200,  fc: 220  },
                  { amt: 5000, b: 500,  s: 1000, fc: 1100 },
                ].map((row) => (
                  <tr key={row.amt} className="border-b border-paper-200">
                    <td className="px-3 py-3 font-mono">${row.amt}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold">
                      ${row.b} + 2% AIML
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold">
                      ${row.s} + 4% AIML
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-gradient">
                      ${row.fc} + 6% AIML
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Level 2 table */}
        <div>
          <h3 className="font-display font-semibold text-lg mb-3">
            🌱 Indirect referrals (Level 2) — your team's friend buys
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-paper-300">
                  <th className="px-3 py-3 text-left eyebrow">They buy</th>
                  <th className="px-3 py-3 text-right eyebrow">Bronze earns</th>
                  <th className="px-3 py-3 text-right eyebrow">Silver earns</th>
                  <th className="px-3 py-3 text-right eyebrow">Founders Circle earns</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { amt: 100,  l2: 2,   fc: 3   },
                  { amt: 500,  l2: 10,  fc: 15  },
                  { amt: 1000, l2: 20,  fc: 30  },
                  { amt: 5000, l2: 100, fc: 150 },
                ].map((row) => (
                  <tr key={row.amt} className="border-b border-paper-200">
                    <td className="px-3 py-3 font-mono">${row.amt}</td>
                    <td className="px-3 py-3 text-right font-mono font-semibold">
                      ${row.l2} + 2% AIML
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold">
                      ${row.l2} + 2% AIML
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-gradient">
                      ${row.fc} + 3% AIML
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-ink-600 mt-4 text-center">
          USDT amounts are exact. AIML bonus shown as a percentage of the purchase value —
          actual tokens depend on the current rate (~8,500 AIML per $1 USDT at presale).
          Project keeps a minimum of 75% of every sale; the remaining 25% covers all commission.
        </p>
      </Card>

      {/* Founders Circle deep-dive */}
      <Card className="mb-16 !border-violet-300 !bg-gradient-to-br from-magenta-50 via-violet-50 to-azure-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="eyebrow text-violet-700 mb-2">🥇 FOUNDERS CIRCLE</div>
            <h2 className="font-display text-3xl font-bold mb-4">
              The leadership tier
            </h2>
            <p className="text-ink-700 mb-4">
              When your team's combined purchases (your referrals + their referrals) hit
              <strong> $3,000</strong>, you join the Founders Circle. Top rates on your direct
              sales, plus an upgraded <strong>3% USDT + 3% AIML leadership override</strong> on
              every indirect sale your team brings in.
            </p>
            <p className="text-ink-700 mb-4">
              Reachable example: 5 friends each at Silver ($300) is $1,500. Each brings
              2 friends who buy $150 average — that's another $1,500.
              <strong> Total team volume: $3,000.</strong> You're Founders.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>22% USDT + 6% AIML on direct referrals</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>3% USDT + 3% AIML on indirect sales (leadership override)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>Passive income that compounds as your team grows its own teams</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-violet-600 font-bold">✓</span>
                <span>First access to every launch and announcement</span>
              </li>
            </ul>
          </div>
          <div className="bg-white/60 rounded-2xl p-6">
            <div className="text-xs uppercase tracking-wider text-violet-700 mb-2">
              Worked example
            </div>
            <p className="text-sm text-ink-700 mb-3">
              You're Founders Circle. Priya is one of your direct referrals (Silver).
              Priya brings in Amit, who buys $1,000:
            </p>
            <div className="space-y-2 text-sm font-mono">
              <div className="flex justify-between">
                <span className="text-ink-600">Priya earns (L1, Silver):</span>
                <span className="font-bold">$200 + 4% AIML</span>
              </div>
              <div className="flex justify-between bg-violet-100 rounded-lg px-3 py-2">
                <span className="text-violet-700 font-semibold">You earn (L2, FC):</span>
                <span className="font-bold text-violet-700">$30 + 3% AIML</span>
              </div>
              <p className="text-xs text-ink-600 italic mt-3">
                $30 sounds small per sale — but as your team scales to dozens of indirect
                referrals each month, the overrides compound into a real second income stream
                that requires no extra effort from you.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* FAQ */}
      <div className="mb-12">
        <div className="text-center mb-8">
          <div className="eyebrow text-violet-600">COMMON QUESTIONS</div>
          <h2 className="font-display text-3xl font-bold mt-2">Quick answers</h2>
        </div>
        <div className="space-y-3">
          {[
            {
              q: 'How do I get paid?',
              a: 'USDT is sent straight to your crypto wallet. You can withdraw to your bank anytime via any exchange. AIML tokens go to the same wallet — hold them or trade after launch (subject to 6-month linear vesting from TGE).',
            },
            {
              q: 'What\'s the difference between Level 1 and Level 2?',
              a: 'Level 1 (L1) is your direct referrals — people who used your link. Level 2 (L2) is your referrals\' referrals — passive income on every sale your team brings in. You earn on both, every time. L1 pays your full tier rate; L2 pays 2% (Bronze/Silver) or 3% (Founders).',
            },
            {
              q: 'How long do my referrals last?',
              a: 'Forever. Once they sign up through your link, they\'re tagged as yours. Every time they buy — today, next year, anytime — you earn at your current tier rate. Same for L2: anyone in your team\'s chain stays in your team\'s chain.',
            },
            {
              q: 'What counts toward the $3,000 team volume for Founders Circle?',
              a: 'Every purchase made by anyone in your downline — both your direct referrals (L1) and their referrals (L2) — counts toward your team volume. It\'s cumulative and never resets.',
            },
            {
              q: 'What if I don\'t refer anyone?',
              a: 'No problem. Your $100 buy is your own AIML investment — you keep those tokens regardless. The referral earnings are bonus on top.',
            },
            {
              q: 'When do tier upgrades happen?',
              a: 'Instantly. The moment you cross a threshold (either by topping up your buy or by your team\'s volume hitting $3k), your new rate applies to all future sales. Tier promotion is sticky — you never get demoted.',
            },
            {
              q: 'Is there a minimum to buy?',
              a: 'No. You can buy any amount of AIML — even $5. The $100/$300 thresholds only matter for affiliate eligibility, not for buying.',
            },
            {
              q: 'What\'s the catch?',
              a: 'Two rules: complete KYC verification before withdrawing (standard for any compliant project), and the affiliate program requires the $100 personal buy to unlock. No locks, no quotas, no expiry. Project keeps a minimum 75% of every sale — the remaining 25% covers all commission outflows.',
            },
          ].map((item) => (
            <details
              key={item.q}
              className="card-base p-5 cursor-pointer group"
            >
              <summary className="font-display font-semibold text-lg list-none flex justify-between items-center">
                {item.q}
                <span className="text-violet-500 group-open:rotate-180 transition-transform">
                  ▼
                </span>
              </summary>
              <p className="mt-3 text-sm text-ink-700">{item.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA bottom */}
      <Card className="text-center !bg-gradient-to-br from-magenta-500 via-violet-500 to-azure-500 !text-white !border-0">
        <h2 className="font-display text-3xl font-bold mb-3">Ready to start earning?</h2>
        <p className="opacity-90 mb-6 max-w-xl mx-auto">
          Sign up, buy $100 of AIML, share your link. Your first commission could be in your
          wallet by tomorrow — and the passive Level 2 earnings start the moment your team grows.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/auth/signup">
            <Button variant="primary" size="lg" className="!bg-white !text-violet-600 hover:!bg-paper-100">
              Create my account
            </Button>
          </Link>
          <Link to="/buy">
            <Button variant="outline" size="lg" className="!border-white !text-white hover:!bg-white/10">
              Buy $AIML first
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}
